
import React, { useRef, useEffect } from 'react';
import * as d3 from 'd3';

interface Node extends d3.SimulationNodeDatum {
  id: string;
  label: string;
  group: number;
  size: number;
}

interface Link {
  source: string | Node;
  target: string | Node;
  value: number;
}

interface NetworkGraphProps {
  data: {
    nodes: Node[];
    links: Link[];
  };
}

const NetworkGraph: React.FC<NetworkGraphProps> = ({ data }) => {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!data || !data.nodes || !data.links || data.nodes.length === 0) {
      return;
    }

    // Clear previous graph if any
    const svgElement = svgRef.current;
    if (svgElement) {
      d3.select(svgElement).selectAll("*").remove();
    }

    // Set the dimensions and margins of the graph
    const width = svgElement?.clientWidth || 600;
    const height = svgElement?.clientHeight || 400;

    // Create a color scale for node groups
    const color = d3.scaleOrdinal<string>()
      .domain(d3.range(1, 7).map(String))
      .range(["#9b87f5", "#f97316", "#0ea5e9", "#d946ef", "#10b981", "#f43f5e"]);

    // Create a force simulation
    const simulation = d3.forceSimulation<Node>()
      .nodes([...data.nodes])
      .force("link", d3.forceLink<Node, Link>(data.links as any).id((d: Node) => d.id).distance(80))
      .force("charge", d3.forceManyBody().strength(-400))
      .force("center", d3.forceCenter(width / 2, height / 2))
      .force("collision", d3.forceCollide().radius((d: Node) => d.size + 10));

    // Create SVG element
    const svg = d3.select(svgRef.current);

    // Add links
    const link = svg.append("g")
      .selectAll("line")
      .data(data.links)
      .join("line")
      .attr("stroke-width", (d: Link) => Math.sqrt(d.value) * 1.5);

    // Add nodes - fixing the radius function to return a number
    const node = svg.append("g")
      .attr("stroke", "#fff")
      .attr("stroke-width", 1.5)
      .selectAll("circle")
      .data(data.nodes)
      .join("circle")
      // Fix: Explicitly return a number for the radius
      .attr("r", (d: Node): number => d.size)
      .attr("fill", (d: Node): string => color(d.group.toString()) as string)
      .call(drag(simulation) as any);

    // Add labels
    const label = svg.append("g")
      .attr("class", "labels")
      .selectAll("text")
      .data(data.nodes)
      .join("text")
      .attr("text-anchor", "middle")
      .attr("font-size", (d: Node): string => d.size > 20 ? "12px" : "10px")
      .attr("fill", "#fff")
      .text((d: Node): string => d.label);

    // Update positions on simulation tick
    simulation.on("tick", () => {
      link
        .attr("x1", d => (d.source as Node).x!)
        .attr("y1", d => (d.source as Node).y!)
        .attr("x2", d => (d.target as Node).x!)
        .attr("y2", d => (d.target as Node).y!);

      node
        .attr("cx", d => d.x!)
        .attr("cy", d => d.y!);

      label
        .attr("x", d => d.x!)
        .attr("y", d => d.y! + d.size + 5);
    });

    // Define drag behavior function
    function drag(simulation: d3.Simulation<Node, undefined>) {
      function dragstarted(event: any, d: Node) {
        if (!event.active) simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      }

      function dragged(event: any, d: Node) {
        d.fx = event.x;
        d.fy = event.y;
      }

      function dragended(event: any, d: Node) {
        if (!event.active) simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      }

      return d3.drag<SVGCircleElement, Node>()
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);
    }

    // Return a cleanup function
    return () => {
      simulation.stop();
    };
  }, [data]);

  return <svg ref={svgRef} width="100%" height="100%" />;
};

export default NetworkGraph;
